// src/components/SurveyResults.jsx with PageWrapper
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LoadingItem from './LoadingItem';
import PageWrapper from './PageWrapper';
import { fetchSurveyResults } from '../server';
import LoadingWrapper from './LoadingWrapper';
import { formatDate } from '../util/utils';
import Check from '../Check';

function SurveyResults() {
	const { surveyCode } = useParams();
	const [loading, setLoading] = useState(true);
	const [results, setResults] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		const getSurveyResults = async () => {
			if (!surveyCode) {
				setError('Survey code is not provided. Results are not available.');
				setLoading(false);
				return;
			}

			try {
				// Call the API to get survey results based on the code
				const response = await fetchSurveyResults(surveyCode);
				setResults(response);
			} catch (err) {
				setError('Error retrieving survey results');
				console.error('Error fetching survey results:', err);
			} finally {
				setLoading(false);
			}
		};

		getSurveyResults();
	}, [surveyCode]);

	if (loading) {
		return <LoadingWrapper />;
	}

	if (error) {
		return (
			<PageWrapper title='Oops! Something happened...'>
				<div className='text-center'>
					<p>{error}</p>
					<p className='mt-[10px]'>Please check the survey code and try again.</p>
					<p className='mt-[20px]'>
						If you continue to experience issues, please contact support at:
						<br />
						<a href='mailto:help@alkalmahealth.com' className='font-bold text-[#153c4f]'>
							help@alkalmahealth.com
						</a>
					</p>
				</div>
			</PageWrapper>
		);
	}

	if (!results || !results.surveyData || !Array.isArray(results.surveyData)) {
		return (
			<PageWrapper title='No Results Available'>
				<div className='text-center'>
					<p>There are no results available for this survey.</p>
				</div>
			</PageWrapper>
		);
	}

	return (
		<PageWrapper title={`Survey Results: AlkalmaUp Baseline Wellbeing Assessment`}>
			<div className='mb-6'>
				{results.schoolName && <p className='text-gray-600 text-lg'>{results.schoolName}</p>}
				{results.userName && <p className='text-gray-600 text-lg'>{results.userName}</p>}
				{results.email && <p className='text-gray-600 text-lg'>{results.email}</p>}
				<p className='text-gray-600 text-lg'>Submitted: {formatDate(results.submittedAt)}</p>
			</div>

			<div className='mb-8'>
				{results.surveyData.map((question, index) => (
					<div key={index} className='mb-8 border-gray-200 last:border-0'>
						<p className='text-2xl font-bold text-[#42405f] mb-3'>
							{index + 1}. {question.title}
						</p>

						{question.selectedValue && question.selectedValue.length > 0 ? (
							<div className='ml-4 text-[rgba(31,31,67,0.7)]'>
								{question.selectedValue.map((answer, answerIdx) => (
									<div key={answerIdx} className='flex items-center mb-2'>
										<div className='flex-shrink-0 mr-2'>
											<Check />
										</div>
										<p className='flex-1'>{answer}</p>
									</div>
								))}
							</div>
						) : (
							<p className='italic text-gray-500'>No answer provided</p>
						)}
					</div>
				))}
			</div>

			<div className='text-center mt-12'>
				<p className='text-[#153c4f] font-bold'>Thank you for using AlkalmaUp!</p>
				<p className='text-gray-600 mt-2'>
					If you have any questions about these results, please contact support at:
					<a href='mailto:help@alkalmahealth.com' className='ml-1 text-[#153c4f]'>
						help@alkalmahealth.com
					</a>
				</p>
			</div>
		</PageWrapper>
	);
}

export default SurveyResults;
